/**
 * Inayah — Color-Grouped Product Gallery Filter (Release theme)
 *
 * Ported from the main store's inayah-gallery-v2.js. Shows only the media that
 * belongs to the currently selected color, matched by each media item's alt text.
 *
 * Why a standalone, delegated listener: the live product template selects color via
 * hand-coded custom-Liquid swatches (.color-swatch) that do NOT dispatch the theme's
 * variant-change event, so we listen for clicks/hover on the swatches directly.
 *
 * Markup it relies on (set in Liquid):
 *   - gallery items:  [data-media-alt] inside .main-product__media   (product-media-card.liquid)
 *   - color swatches: .color-swatch[data-color]                       (custom_liquid color block)
 *   - selected color: #selected-color-name textContent                (custom_liquid color block)
 */
(function () {
  'use strict';

  var GALLERY_ROOT = '.main-product__media';
  var GALLERY_ITEM_SELECTOR = GALLERY_ROOT + ' [data-media-alt]';
  var SWATCH_SELECTOR = '.color-swatch';

  function normalize(s) {
    return (s || '').toString().trim().toLowerCase();
  }

  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // True when the media's alt text "belongs to" the given color. We accept either an
  // alt that starts with the color ("Oat front") or the color appearing as a whole
  // word/token ("Off White Floral" for color "White Floral"). Word-boundary matching
  // avoids false hits like color "Tan" matching a "suntan" lifestyle shot.
  function altMatchesColor(altText, color) {
    if (!color) return true;
    var a = normalize(altText);
    var c = normalize(color);
    if (!a) return false;
    if (a.indexOf(c) === 0) return true;
    var pattern = new RegExp('(^|[^a-z0-9])' + escapeRe(c) + '([^a-z0-9]|$)');
    return pattern.test(a);
  }

  // The product's colours (from the swatches), cached. Used to spot group / lifestyle shots
  // whose alt text names more than one colour — those are universal.
  var PRODUCT_COLORS = null;
  function productColors() {
    if (PRODUCT_COLORS) return PRODUCT_COLORS;
    var set = [];
    document.querySelectorAll(SWATCH_SELECTOR).forEach(function (s) {
      var c = (s.getAttribute('data-color') || s.getAttribute('data-value') || s.title || '').trim();
      if (c && set.indexOf(c) < 0) set.push(c);
    });
    PRODUCT_COLORS = set;
    return set;
  }
  function altColorCount(alt) {
    if (!alt) return 0;
    var colors = productColors();
    var count = 0;
    for (var i = 0; i < colors.length; i++) {
      if (altMatchesColor(alt, colors[i])) count++;
    }
    return count;
  }

  function getSelectedColor() {
    var label = document.getElementById('selected-color-name');
    var v = label ? (label.textContent || '').trim() : '';
    return v || null;
  }

  // Nudge the Swiper instances so the slider/thumbs re-layout after we hide items.
  function refreshSwipers() {
    document
      .querySelectorAll(GALLERY_ROOT + ' swiper-product-gallery, ' + GALLERY_ROOT + ' swiper-product-thumbs')
      .forEach(function (el) {
        try {
          var sw = el.instance;
          if (!sw || typeof sw.update !== 'function') return;
          sw.update();
          // Land on the first visible (matching) slide so the slider never sits on a
          // hidden/blank cell, and refresh the pagination so the dot count matches.
          if (typeof sw.slideTo === 'function') sw.slideTo(0, 0);
          if (sw.pagination && typeof sw.pagination.render === 'function') {
            sw.pagination.render();
            sw.pagination.update();
          }
        } catch (e) {}
      });
  }

  var IS_APPLYING = false;
  var LAST_APPLIED_COLOR = null;
  var LAST_APPLIED_HIDDEN_COUNT = -1;

  // Toggle a media item's visibility. On the desktop grid each media is wrapped in its
  // own .main-product__media--grid-item cell; we must hide that wrapper too, otherwise
  // it leaves an empty grid cell (and pushes the visible image into the wrong column).
  function setItemDisplay(el, hide) {
    if (hide) el.style.setProperty('display', 'none', 'important');
    else el.style.removeProperty('display');
    var gridItem = el.closest('.main-product__media--grid-item');
    if (gridItem) {
      if (hide) gridItem.style.setProperty('display', 'none', 'important');
      else gridItem.style.removeProperty('display');
    }
  }

  function showAll(items) {
    items.forEach(function (el) { setItemDisplay(el, false); });
  }

  // Decide whether a gallery item belongs to the given color. Priority:
  //  1) authoritative — the variant->image map keyed by media id (window.inayahMediaColor),
  //     built in Liquid from variant.featured_media (the real Shopify variant->image link),
  //  2) alias — an alt text a variant's featured image used for this color
  //     (window.inayahAltColor), e.g. alt "brown" learned to mean option "Chocolate",
  //  3) fuzzy — alt text starts-with / whole-word match (legacy; covers products that have
  //     no assigned variant images).
  function itemMatchesColor(el, color) {
    if (!color) return true;
    var maps = window.inayahMediaColor;
    var id = el.getAttribute('data-media-id');
    if (id && maps && Object.prototype.hasOwnProperty.call(maps, id)) {
      return normalize(maps[id]) === normalize(color);
    }
    var alt = el.getAttribute('data-media-alt') || '';
    if (!alt) {
      var media = el.querySelector('img, video');
      alt = media ? (media.getAttribute('alt') || media.getAttribute('aria-label') || '') : '';
    }
    var aliasMap = window.inayahAltColor;
    var altN = normalize(alt);
    if (altN && aliasMap && Object.prototype.hasOwnProperty.call(aliasMap, altN)) {
      return normalize(aliasMap[altN]) === normalize(color);
    }
    // Universal unless the alt names EXACTLY ONE of the product's colours. A count of 0 (no alt,
    // a lifestyle shot, or a generic product-title alt like "the everyday hijab") and a count of
    // 2+ (a group shot of several colours) both mean the image isn't tied to a single colour — so
    // it shows on EVERY colour selection instead of vanishing. Only a single-colour alt is filtered.
    if (altColorCount(alt) !== 1) return true;
    return altMatchesColor(alt, color);
  }

  function applyFilter(color) {
    if (IS_APPLYING) return;
    var items = document.querySelectorAll(GALLERY_ITEM_SELECTOR);
    if (!items.length) return;

    IS_APPLYING = true;
    try {
      if (!color) {
        showAll(items);
        LAST_APPLIED_COLOR = null;
        LAST_APPLIED_HIDDEN_COUNT = 0;
        return;
      }

      var matched = 0;
      var decisions = [];
      items.forEach(function (el) {
        var ok = itemMatchesColor(el, color);
        if (ok) matched++;
        decisions.push({ el: el, ok: ok });
      });

      // Safety net: if the selected color matches no media (e.g. images alt-tagged with a
      // name that differs from the option value), show everything rather than an empty gallery.
      if (matched === 0) {
        showAll(items);
        LAST_APPLIED_COLOR = color;
        LAST_APPLIED_HIDDEN_COUNT = 0;
        refreshSwipers();
        return;
      }

      var hiddenCount = items.length - matched;
      if (LAST_APPLIED_COLOR === color && LAST_APPLIED_HIDDEN_COUNT === hiddenCount) return;

      decisions.forEach(function (d) { setItemDisplay(d.el, !d.ok); });
      LAST_APPLIED_COLOR = color;
      LAST_APPLIED_HIDDEN_COUNT = hiddenCount;
      refreshSwipers();
    } finally {
      setTimeout(function () { IS_APPLYING = false; }, 0);
    }
  }

  function revertToSelected() {
    var c = getSelectedColor();
    if (c) applyFilter(c);
    else showAll(document.querySelectorAll(GALLERY_ITEM_SELECTOR));
  }

  function swatchFromEvent(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    var s = t.closest(SWATCH_SELECTOR);
    if (!s) return null;
    var v = s.getAttribute('data-color') || (s.getAttribute('data-value') || s.title || '').trim();
    return v ? { el: s, color: v.trim() } : null;
  }

  function onSwatchClick(e) {
    var info = swatchFromEvent(e);
    if (!info) return;
    // Run twice: once immediately, once after the swatch's own handler updates state.
    applyFilter(info.color);
    setTimeout(function () { applyFilter(info.color); }, 120);
  }

  function onSwatchMouseOver(e) {
    var info = swatchFromEvent(e);
    if (!info) return;
    applyFilter(info.color);
  }

  function onSwatchMouseOut(e) {
    var info = swatchFromEvent(e);
    if (!info) return;
    var to = e.relatedTarget;
    if (to && info.el.contains(to)) return;
    if (to && to.closest && to.closest(SWATCH_SELECTOR)) return;
    revertToSelected();
  }

  function init() {
    if (!document.querySelector(SWATCH_SELECTOR)) return; // no color swatches on this page
    revertToSelected();
    // Hover-preview ONLY on devices that truly hover (desktop). On a touch screen a bound mouseover
    // makes the FIRST tap register as a hover — the gallery previews the colour but the browser holds
    // the real click for a SECOND tap, so the swatch/variant/price only selects on the 2nd tap.
    // Touch devices get click-only, so a single tap both swaps the photo and selects the variant.
    var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;
    if (canHover) {
      document.addEventListener('mouseover', onSwatchMouseOver, true);
      document.addEventListener('mouseout', onSwatchMouseOut, true);
    }
    document.addEventListener('click', onSwatchClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
